"use client";

import { CopyField } from "@commerce/components/CopyField";
import { useCopy } from "@commerce/components/use-copy";
import { Button } from "@ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import { CopyIcon, TriangleAlertIcon } from "lucide-react";
import { useState } from "react";

export interface RevealedSecret {
	title: string;
	/** Under the title; "You will not see it again." when absent. */
	description?: string;
	/** Names it ("API key", "Webhook secret"); "Signing secret" when absent. */
	label?: string;
	secret: string;
	/** What the person must do next with it. */
	note: string;
	/** Values to paste next to the secret, like a delivery URL. Shown above it. */
	fields?: { label: string; value: string }[];
}

/**
 * Shows a secret (signing secret, API key, catalog webhook secret) the only time the server
 * returns it. It cannot be dismissed by accident: it closes only once the secret was copied
 * (or copying failed, since it can still be copied by hand).
 */
export function SecretDialog({
	revealed,
	onClose,
}: {
	revealed: RevealedSecret | null;
	onClose: () => void;
}) {
	const [copied, setCopied] = useState(false);
	const { copy } = useCopy(revealed?.secret ?? "", () => setCopied(true));
	const close = () => {
		setCopied(false);
		onClose();
	};
	const label = revealed?.label ?? "Signing secret";
	const fields = revealed?.fields ?? [];

	return (
		<Dialog
			open={revealed !== null}
			onOpenChange={(open) => {
				if (!open && copied) {
					close();
				}
			}}
		>
			<DialogContent showCloseButton={false} className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{revealed?.title}</DialogTitle>
					<DialogDescription>
						{revealed?.description ?? "You will not see it again."}
					</DialogDescription>
				</DialogHeader>
				{fields.map((field) => (
					<CopyField
						key={field.label}
						label={field.label}
						value={field.value}
					/>
				))}
				<CopyField
					label={label}
					// Alone, the title already says what it is.
					hideLabel={fields.length === 0}
					value={revealed?.secret ?? ""}
					onCopied={() => setCopied(true)}
				/>
				<p className="flex items-start gap-2 text-muted-foreground text-sm">
					<TriangleAlertIcon
						className="mt-0.5 size-4 shrink-0 text-warning"
						aria-hidden
					/>
					{revealed?.note}
				</p>
				<DialogFooter>
					{copied ? (
						<Button onClick={close}>I've saved it</Button>
					) : (
						<Button onClick={copy}>
							<CopyIcon data-icon="inline-start" />
							Copy
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
